import { useEffect, useState } from "react";

export function useDebounce<T>(value: T, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = window.setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Cancel the timeout if value changes (also on delay change or unmount)
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return { debouncedValue, setDebouncedValue };
}

export type DebounceState<T> = {
  value: T;
  setDelayed: React.Dispatch<React.SetStateAction<T>>;
  setImmediate: (value: T) => void;
  debouncedValue: T;
};

export function useDebounceState<T>(
  initialValue: T,
  delay = 500,
): DebounceState<T> {
  const [value, setValue] = useState<T>(initialValue);
  const { debouncedValue, setDebouncedValue } = useDebounce(value, delay);

  return {
    value,
    setDelayed: setValue,
    setImmediate: (value: T) => {
      setDebouncedValue(value);
      setValue(value);
    },
    debouncedValue,
  };
}
